import React from 'react';
import styled from 'styled-components';
import useControlledState from '@quid/react-use-controlled-state';
import { globalTheme, useMixin } from '@clds/component-theme';
import { ColorPickerPopover } from './components';
import { ColorItem } from './components/ColorItem';
import { colorPickerTheme } from './theme';
import { StyledHex } from './components/HexInput';
import { isValidHex, parseHexInputValueByAlpha } from './colorPickerUtils';

const FieldWrapper = styled.div`
  display: flex;
`;

const ColorItemWrapper = styled(ColorItem)`
  margin-left: ${globalTheme.spacing.xxs};
`;

export const ColorPickerVariant = { DEFAULT: 'default', SIMPLE_HEX: 'simpleHex' };

/**
 * Readonly `TextField` shown value and visual representation of a color chosen
 * from `ColorPickerPopover` in hex 6 digit hex format or with 8 digits if there's alpha
 * @param initialColor: string
 * @param color: string
 * @param onChange: (color: [string]) => void
 * @param onChangeComplete: (color: [string]) => void
 * @param size?: 'xs' | 'md'
 * @param validationState?: 'error' | 'warning' | 'success' | 'none'
 * @param isReadonly?: boolean
 * @returns {JSX.Element}
 * @constructor
 */
const ColorFieldBase = ({
  initialColor,
  color,
  onChange: onChangeCustom,
  onChangeComplete,
  size = 'xs',
  id,
  validationState,
  variant,
  isReadonly = true,
}) => {
  const [selectedColor, setSelectedColor] = useControlledState(initialColor, color, onChangeCustom);
  const [cachedColor, setCachedColor] = React.useState(null);

  const editableInputProps = {
    onChange: setSelectedColor,
    onFocus: () => setCachedColor(selectedColor),
    onBlur: () => {
      if (!isValidHex(selectedColor, variant !== ColorPickerVariant.SIMPLE_HEX)) {
        setSelectedColor(cachedColor);
        setCachedColor(null);
      } else {
        setSelectedColor(parseHexInputValueByAlpha(selectedColor));
      }
    },
  };

  return (
    <FieldWrapper>
      <StyledHex
        type="text"
        id={id}
        value={selectedColor}
        size={size}
        validationState={validationState}
        isReadonly={isReadonly}
        {...(!isReadonly && editableInputProps)}
      />
      <ColorPickerPopover
        color={selectedColor}
        onChange={(color) => setSelectedColor(color)}
        onChangeComplete={onChangeComplete}
        triggeredElement={<ColorItemWrapper value={selectedColor} size={useMixin(colorPickerTheme.field.sizeVariant[size].textField)} />}
        variant={variant}
      />
    </FieldWrapper>
  );
};

export const ColorField = (props) => <ColorFieldBase {...props} variant={ColorPickerVariant.DEFAULT} />;
export const SimpleHexColorField = (props) => <ColorFieldBase {...props} variant={ColorPickerVariant.SIMPLE_HEX} />;
